/**
 * ReportExportPage — on-demand CSV/XLSX export configurator.
 * Left: "New Export" form (report type, date range, filters, columns, format).
 * Right: "Recent exports" panel (job cards, status badges, Download button, 5s auto-poll).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	COLUMN_PRESETS,
	DEFAULT_COLUMNS,
	REPORT_TYPES,
	type ExportFormat,
	type ExportJob,
	type ExportStatus,
	type ReportType,
} from "./api";
import {
	useReportExportCreate,
	useReportExportDownload,
	useReportExportList,
} from "./hooks";

const CARD = "bg-white border border-zinc-200 rounded-lg shadow-sm p-6";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const INPUT =
	"w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const BTN_PRIMARY =
	"bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY =
	"bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed";
const HELPER = "text-xs text-zinc-500 mt-1";

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n: number): string {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return d.toISOString().slice(0, 10);
}

interface FilterRow {
	key: string;
	value: string;
}

const FILTER_KEYS_BY_TYPE: Record<ReportType, string[]> = {
	clicks: ["country", "device", "campaign_id", "publisher_id", "sub_id"],
	conversions: ["status", "campaign_id", "publisher_id", "sub_id"],
	payouts: ["status", "publisher_id", "period"],
	publisher_performance: ["publisher_id", "min_clicks"],
	campaign_performance: ["campaign_id", "advertiser_id"],
};

function StatusBadge({ status }: { status: ExportStatus }) {
	const base = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";
	const map: Record<ExportStatus, string> = {
		queued: "bg-zinc-100 text-zinc-600",
		running: "bg-sky-50 text-sky-700",
		ready: "bg-emerald-50 text-emerald-700",
		failed: "bg-red-50 text-red-700",
	};
	return (
		<span
			className={`${base} ${map[status]}`}
			data-testid={`report-export-status-${status}`}
		>
			{status}
		</span>
	);
}

function ProgressBar() {
	return (
		<div
			className="mt-2 h-1 w-full overflow-hidden rounded-full bg-sky-100"
			data-testid="report-export-progress"
			role="progressbar"
			aria-label="export running"
		>
			<div className="h-full w-1/3 animate-pulse bg-sky-500" />
		</div>
	);
}

function JobCard({ job }: { job: ExportJob }) {
	const downloadMut = useReportExportDownload();
	const onDownload = () => {
		downloadMut.mutate(job.id, {
			onSuccess: ({ url }) => {
				if (typeof window !== "undefined" && url) {
					window.open(url, "_blank", "noopener");
				}
			},
		});
	};
	const label =
		REPORT_TYPES.find((t) => t.value === job.report_type)?.label ?? job.report_type;
	const created = new Date(job.created_at).toLocaleString();
	const rows = typeof job.rows === "number" ? job.rows.toLocaleString() : "—";
	return (
		<article
			className="rounded-lg border border-zinc-200 bg-white p-4"
			data-testid="report-export-job-card"
			data-job-id={job.id}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-sm font-medium text-zinc-900">{label}</p>
					<p className="mt-0.5 text-xs text-zinc-500">
						{job.format.toUpperCase()} · {created}
					</p>
				</div>
				<StatusBadge status={job.status} />
			</div>
			<dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
				<div>
					<dt className="inline">Rows: </dt>
					<dd className="inline font-medium text-zinc-700">{rows}</dd>
				</div>
				<div className="truncate">
					<dt className="inline">Job: </dt>
					<dd className="inline font-mono text-zinc-700">{job.id}</dd>
				</div>
			</dl>
			{job.status === "running" || job.status === "queued" ? <ProgressBar /> : null}
			{job.status === "failed" && job.error ? (
				<p className="mt-2 text-xs text-red-600" role="alert">
					{job.error}
				</p>
			) : null}
			{job.status === "ready" ? (
				<div className="mt-3 flex justify-end">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onDownload}
						disabled={downloadMut.isPending}
						data-testid="report-export-download"
						data-job-id={job.id}
					>
						{downloadMut.isPending ? "Preparing." : "Download"}
					</button>
				</div>
			) : null}
		</article>
	);
}

export function ReportExportPage() {
	const queueMut = useReportExportCreate();
	const listQ = useReportExportList();

	const [reportType, setReportType] = useState<ReportType>("clicks");
	const [format, setFormat] = useState<ExportFormat>("csv");
	const [dateFrom, setDateFrom] = useState<string>(daysAgoIso(7));
	const [dateTo, setDateTo] = useState<string>(todayIso());
	const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS.clicks);
	const [filters, setFilters] = useState<FilterRow[]>([]);

	const availableColumns = useMemo(() => COLUMN_PRESETS[reportType], [reportType]);
	const availableFilterKeys = useMemo(
		() => FILTER_KEYS_BY_TYPE[reportType],
		[reportType],
	);

	const handleReportTypeChange = (next: ReportType) => {
		setReportType(next);
		setColumns(DEFAULT_COLUMNS[next]);
		setFilters([]);
	};

	const toggleColumn = (c: string) => {
		setColumns((prev) =>
			prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
		);
	};

	const addFilter = () => {
		const firstKey = availableFilterKeys[0] ?? "";
		setFilters((prev) => [...prev, { key: firstKey, value: "" }]);
	};

	const updateFilter = (idx: number, patch: Partial<FilterRow>) => {
		setFilters((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
	};

	const removeFilter = (idx: number) => {
		setFilters((prev) => prev.filter((_, i) => i !== idx));
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (columns.length === 0) return;
		const filterMap: Record<string, unknown> = {};
		for (const row of filters) {
			const k = row.key.trim();
			const v = row.value.trim();
			if (k && v) filterMap[k] = v;
		}
		queueMut.mutate(
			{
				report_type: reportType,
				format,
				filters: filterMap,
				columns,
				date_from: dateFrom,
				date_to: dateTo,
			},
			{
				onSuccess: () => {
					// keep selections — operators frequently re-run with small tweaks
					setFilters([]);
				},
			},
		);
	};

	const items = listQ.data?.items ?? [];

	return (
		<main
			data-testid="report-export-page"
			className="mx-auto w-full max-w-7xl px-6 py-6"
		>
			<header className="mb-6 flex flex-col gap-1">
				<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-2xl font-semibold text-transparent">
					Exports
				</h1>
				<p className="text-sm text-zinc-500">
					Queue on-demand CSV or XLSX exports. Download links appear here when ready.
				</p>
			</header>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
				<section className={`${CARD} lg:col-span-3`} aria-labelledby="new-export-heading">
					<h2
						id="new-export-heading"
						className="mb-4 text-base font-medium text-zinc-900"
					>
						New Export
					</h2>
					<form
						onSubmit={handleSubmit}
						className="flex flex-col gap-4"
						data-testid="report-export-form"
						aria-label="report-export form"
					>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<label htmlFor="re-report-type" className={LABEL}>
									Report type
								</label>
								<select
									id="re-report-type"
									className={INPUT}
									value={reportType}
									onChange={(e) =>
										handleReportTypeChange(e.currentTarget.value as ReportType)
									}
									data-testid="report-export-type"
								>
									{REPORT_TYPES.map((t) => (
										<option key={t.value} value={t.value}>
											{t.label}
										</option>
									))}
								</select>
							</div>
							<div>
								<span className={LABEL}>Format</span>
								<div className="flex gap-4 pt-2">
									{(["csv", "xlsx"] as ExportFormat[]).map((f) => (
										<label
											key={f}
											className="flex items-center gap-2 text-sm text-zinc-700"
										>
											<input
												type="radio"
												name="format"
												value={f}
												checked={format === f}
												onChange={() => setFormat(f)}
												className="accent-emerald-500"
												data-testid={`report-export-format-${f}`}
											/>
											{f.toUpperCase()}
										</label>
									))}
								</div>
							</div>
							<div>
								<label htmlFor="re-date-from" className={LABEL}>
									Date from
								</label>
								<input
									id="re-date-from"
									type="date"
									className={INPUT}
									value={dateFrom}
									onChange={(e) => setDateFrom(e.currentTarget.value)}
									required
									data-testid="report-export-date-from"
								/>
							</div>
							<div>
								<label htmlFor="re-date-to" className={LABEL}>
									Date to
								</label>
								<input
									id="re-date-to"
									type="date"
									className={INPUT}
									value={dateTo}
									onChange={(e) => setDateTo(e.currentTarget.value)}
									required
									data-testid="report-export-date-to"
								/>
							</div>
						</div>

						<fieldset className="border-t border-zinc-200 pt-4">
							<legend className="text-sm font-medium text-zinc-700">Filters</legend>
							<p className={HELPER}>Filters are specific to the selected report type.</p>
							<div
								className="mt-3 flex flex-col gap-2"
								data-testid="report-export-filters"
							>
								{filters.map((f, idx) => (
									<div key={idx} className="flex flex-wrap items-center gap-2">
										<select
											className={`${INPUT} max-w-[12rem]`}
											value={f.key}
											onChange={(e) =>
												updateFilter(idx, { key: e.currentTarget.value })
											}
											data-testid={`report-export-filter-key-${idx}`}
										>
											{availableFilterKeys.map((k) => (
												<option key={k} value={k}>
													{k}
												</option>
											))}
										</select>
										<input
											type="text"
											className={`${INPUT} max-w-xs`}
											placeholder="value"
											value={f.value}
											onChange={(e) =>
												updateFilter(idx, { value: e.currentTarget.value })
											}
											data-testid={`report-export-filter-value-${idx}`}
										/>
										<button
											type="button"
											className={BTN_SECONDARY}
											onClick={() => removeFilter(idx)}
											data-testid={`report-export-filter-remove-${idx}`}
											aria-label={`remove filter ${idx + 1}`}
										>
											Remove
										</button>
									</div>
								))}
								<button
									type="button"
									className={`${BTN_SECONDARY} self-start`}
									onClick={addFilter}
									disabled={availableFilterKeys.length === 0}
									data-testid="report-export-filter-add"
								>
									Add filter
								</button>
							</div>
						</fieldset>

						<fieldset className="border-t border-zinc-200 pt-4">
							<legend className="text-sm font-medium text-zinc-700">Columns</legend>
							<p className={HELPER}>
								Pick the columns included in the export. Defaults are pre-selected.
							</p>
							<div
								className="mt-3 flex flex-wrap gap-3"
								data-testid="report-export-columns"
							>
								{availableColumns.map((c) => {
									const checked = columns.includes(c);
									return (
										<label
											key={c}
											className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${
												checked
													? "border-emerald-500 bg-emerald-50 text-emerald-700"
													: "border-zinc-200 text-zinc-700 hover:bg-zinc-50"
											}`}
										>
											<input
												type="checkbox"
												className="accent-emerald-500"
												checked={checked}
												onChange={() => toggleColumn(c)}
												data-testid={`report-export-column-${c}`}
											/>
											{c}
										</label>
									);
								})}
							</div>
							{columns.length === 0 ? (
								<p className="text-xs text-red-600 mt-2" role="alert">
									Pick at least one column.
								</p>
							) : null}
						</fieldset>

						<div className="flex items-center justify-end gap-3 pt-2">
							{queueMut.error ? (
								<p className="text-sm text-red-600" role="alert">
									{queueMut.error.message}
								</p>
							) : null}
							<button
								type="submit"
								className={BTN_PRIMARY}
								disabled={queueMut.isPending || columns.length === 0}
								data-testid="report-export-submit"
							>
								{queueMut.isPending ? "Submitting." : "Submit export"}
							</button>
						</div>
					</form>
				</section>

				<aside
					className={`${CARD} lg:col-span-2`}
					aria-labelledby="recent-exports-heading"
				>
					<div className="mb-4 flex items-center justify-between">
						<h2
							id="recent-exports-heading"
							className="text-base font-medium text-zinc-900"
						>
							Recent exports
						</h2>
						{listQ.isFetching ? (
							<span className="text-xs text-zinc-400">refreshing.</span>
						) : null}
					</div>
					{listQ.isLoading ? (
						<div
							className="flex flex-col gap-3"
							data-testid="report-export-loading"
						>
							<div className="h-16 animate-pulse rounded-md bg-zinc-100" />
							<div className="h-16 animate-pulse rounded-md bg-zinc-100" />
							<div className="h-16 animate-pulse rounded-md bg-zinc-100" />
						</div>
					) : listQ.error ? (
						<p className="text-sm text-red-600" role="alert">
							{listQ.error.message}
						</p>
					) : items.length === 0 ? (
						<div
							className="flex flex-col items-center py-10 text-center"
							data-testid="report-export-empty"
						>
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
								<span aria-hidden="true" className="text-lg font-semibold">
									E
								</span>
							</div>
							<p className="mt-4 text-base font-medium text-zinc-900">
								No exports yet
							</p>
							<p className="mt-2 max-w-sm text-sm text-zinc-500">
								Submit the form on the left to queue your first export. Links land
								here when jobs finish.
							</p>
						</div>
					) : (
						<div
							className="flex flex-col gap-3"
							data-testid="report-export-list"
						>
							{items.map((j) => (
								<JobCard key={j.id} job={j} />
							))}
						</div>
					)}
				</aside>
			</div>
		</main>
	);
}

export default ReportExportPage;
